import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import formatPhoneNumber from "../utils/formatPhoneNumber";
import { useParams, useNavigate } from "react-router-dom";
import { DatePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';

const UserProfileForm = ({ user }) => {
  const [updatedUser, setUpdatedUser] = useState({
    firstname: user.firstname || "",
    lastname: user.lastname || "",
    phoneno: user.phoneno || "",
    email: user.email || "",
    dob: user.dob ? new Date(user.dob) : "",  
    zipcode: user.zipcode || "",
  });

  const [errors, setErrors] = useState({});
  const [isEdited, setIsEdited] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if the user has edited any field
    const hasChanges = Object.keys(updatedUser).some(
      (key) => updatedUser[key] !== user[key]
    );
    setIsEdited(hasChanges);
  }, [updatedUser, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser({
      ...updatedUser,
      [name]: value,
    });
    setIsEdited(true); // Mark as edited on change
  };

  const formatDate = (date) => {
    if (!date) return "";
    const event = new Date(date);
    const year = event.getUTCFullYear();
    const month = String(event.getUTCMonth() + 1).padStart(2, "0");
    const day = String(event.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateEmail = (email) => {
    // Updated regex to enforce lowercase domain and allow valid TLDs (like .com, .org, .net)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    const reservedKeywords = ["email.com", "support@example.com"]; // Reserved keywords
    
    // Check if the email exceeds the general length limit (254 characters)
    if (email.length > 30) {
      return false;
    }
  
    // Check if the email matches the regex
    if (!emailRegex.test(email)) {
      return false;
    }
  
    // Split the email into the username and domain
    const [username, domain] = email.split("@");
  
    if (!username || !domain) {
      return false; // Missing username or domain
    }
  
    // Check for multiple dots or leading/trailing dots
    if (username.includes("..") || domain.includes("..") || username.startsWith(".") || username.endsWith(".") || domain.startsWith(".") || domain.endsWith(".")) {
      return false;
    }
  
    // Ensure the domain does not contain reserved keywords
    if (reservedKeywords.includes(domain)) {
      return false;
    }
  
    // Check if the domain contains a valid single TLD and nothing extra after that
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1]; // Get the last part (TLD)
    
    // Valid top-level domains (you can add more TLDs to this list)
    const validTLDs = ['com', 'org', 'net'];
    
    // If there are more than 2 parts (e.g. user@something.co.uk) or invalid TLD
    if (domainParts.length > 2 || !validTLDs.includes(tld)) {
      return false;
    }
  
    return true;
  }

  const validateZipCode = (zipcode) => {
    const regex = /^\d{5}(-\d{4})?$/;
    return regex.test(zipcode);
};

  // Original date string validation function
  const isValidDateString = (dob) => {
    const datePattern = /^\d{2}-\d{2}-\d{4}$/;
    if (!datePattern.test(dob)) {
        return false;
    }

    const [day, month, year] = dob.split('-').map(Number);
    
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return false;
    }
    
    const monthDays = [31, (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day > monthDays[month - 1]) {
        return false;
    }
    
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
        return false;
    }

    return true;
  };

  // New function to handle Date objects
  const convertAndValidateDate = (dob) => {
    // Check if the date is a valid Date object
    if (!dob || !(dob instanceof Date) || isNaN(dob.getTime())) {
      return false;
    }
  
    // Get day, month, and year from the date object
    const day = String(dob.getDate()).padStart(2, '0');
    const month = String(dob.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = dob.getFullYear();
  
    const formattedDate = `${day}-${month}-${year}`;
    return isValidDateString(formattedDate);
  };

const validateForm = () => {
  const newErrors = {};

  // Check for empty fields and set error messages
  if (!updatedUser.firstname) {
      newErrors.firstname = "First name is required";
      toast.error(newErrors.firstname);
  } else if (updatedUser.firstname.length > 30) {
      newErrors.firstname = "First name must be up to 30 characters long";
      toast.error(newErrors.firstname);
  }

  if (!updatedUser.lastname) {
      newErrors.lastname = "Last name is required";
      toast.error(newErrors.lastname);
  } else if (updatedUser.lastname.length > 30) {
      newErrors.lastname = "Last name must be up to 30 characters long";
      toast.error(newErrors.lastname);
  }

  if (!updatedUser.email) {
      newErrors.email = "Email is required";
      toast.error(newErrors.email);
  } else if (!validateEmail(updatedUser.email)) {
      newErrors.email = "Invalid email format";
      toast.error(newErrors.email);
  }

  if (!updatedUser.zipcode) {
    newErrors.zipcode = "Zip code is required";
    toast.error(newErrors.zipcode);
  } else if (!/^\d+$/.test(updatedUser.zipcode)) {
      newErrors.zipcode = "Zip code must only contain integers";
      toast.error(newErrors.zipcode);
  } else if (!validateZipCode(updatedUser.zipcode)) {
      newErrors.zipcode = "Zip code must be 5 digits long";
      toast.error(newErrors.zipcode);
  }

  if (!updatedUser.dob) {
    newErrors.dob = "Date of birth is required";
    toast.error(newErrors.dob);
  } else if (!convertAndValidateDate(new Date(updatedUser.dob))) {
      newErrors.dob = "Invalid date of birth. Month and day must be valid.";
      toast.error(newErrors.dob);
  }

  // Update errors state
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0; // Return true if no errors
};
  
const convertToUTC = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

const onSave = async (e) => {
  e.preventDefault();
  try {
      if (!isEdited) {
          toast.info("No changes detected. Please update user profile before saving.");
          return; 
      }

      if (!isSaved && validateForm()) {
        
        const dataToSend = {
          ...updatedUser,
          dob: convertToUTC(new Date(updatedUser.dob)).toISOString(), 
        };
          // API call to update user
          const request = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/users/edituser/${id}`,
              dataToSend,
          );

          if (request.data.success) {
              toast.success(request.data.message);
              setIsSaved(true);
              setTimeout(() => {
                  navigate('/users');
              }, 3000);
          } else {
              toast.error(request.data.message);
          }
      }
  } catch (error) {
      console.log(error, "error");
      toast.error("Network connection lost. Please check your connection and try again.");
  }
};


  return (
    <div style={styles.container}>
      <ToastContainer />
      <div style={styles.header}>
        <h2 style={styles.title}>Update User Profile</h2>
        <div style={styles.buttonContainer}>
          <button 
            style={styles.editButton} 
            onClick={onSave} 
          >
            Save
          </button>
        </div>
      </div>
      <div style={styles.infoContainer}>
        <div style={styles.infoRow}>
          <span style={styles.span}>First Name</span>
          <input
            type="text"
            name="firstname"
            value={updatedUser.firstname}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>

        <div style={styles.infoRow}>
          <span style={styles.span}>Last Name</span>
          <input
            type="text"
            name="lastname"
            value={updatedUser.lastname}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>

        <div style={styles.infoRow}>
          <span style={styles.span}>Phone Number</span>
          <input
            type="text"
            name="phoneno"
            value={formatPhoneNumber(updatedUser.phoneno)}
            onChange={handleInputChange}
            style={styles.input}
            readOnly
          />
        </div>

        <div style={styles.infoRow}>
          <span style={styles.span}>Email</span>
          <input
            type="email"
            name="email"
            value={updatedUser.email}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>

        <div style={styles.infoRow}>
          <span style={styles.span}>Birth Date</span>
          <DatePicker
            value={updatedUser.dob ? new Date(updatedUser.dob) : null}
            onChange={(date) => setUpdatedUser({ ...updatedUser, dob: date })} 
            format="MM/dd/yyyy" 
            style={{ width: '60%'}}
          />
        </div>

        <div style={styles.infoRow}>
          <span style={styles.span}>Zip Code</span>
          <input
            type="text"
            name="zipcode"
            value={updatedUser.zipcode}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: "1000px",
    border: "0px solid #ccc",
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    backgroundColor: "#fff",
    fontFamily: "Poppins",
  },
  header: {
    backgroundColor: "#064260",
    padding: "20px 35px",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    margin: 0,
    fontSize: "21px",
    fontWeight: 600,
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
  },
  infoContainer: {
    padding: "0",
    border: "0",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "15px 35px",
    borderBottom: "1px solid #eee",
    fontSize: "1em",
  },
  span: {
    margin: 0,
    fontSize: "17px",
    fontWeight: 600,
  },
  input: {
    width: '60%',
    padding: '8px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '16px',
    fontFamily: 'Poppins',
    backgroundColor: '#F4F4F6',
    color: '#808080',
  },
  editButton: {
    backgroundColor: "#27B99A",
    color: "#fff",
    border: "none",
    padding: "8px 10px",
    borderRadius: "35px",
    cursor: "pointer",
    width: "80px",
    fontSize: "10px",
    fontWeight: 500,
  },
};

export default UserProfileForm;
